"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Conversions = void 0;
const tableLimits_1 = require("../tableLimits");
const util_1 = require("../util");
const assert_1 = require("./assert");
const NO_ROTATION = { x: 0, y: 0, z: 0 };
const DEG_TO_RAD = Math.PI / 180.0;
/** A wheel whose share of the spin is within this of zero cannot limit it (v1). */
const snap = (denominator) => (Math.abs(denominator) < 1.0e-8 ? 0 : denominator);
class Conversions {
    model;
    engine;
    geometry;
    mechanism;
    /** v1's limits.RPM: wheel RPM band and most spin. */
    rpmLimits;
    constructor(model, engine) {
        this.model = model;
        this.engine = engine;
        this.geometry = engine.profile.trainer.geometry;
        this.mechanism = engine.profile.trainer.mechanism;
        this.rpmLimits = tableLimits_1.tableLimitsMap[model.name][model.sport].RPM;
    }
    yawpitch2launchpoint(yawDeg, pitchDeg) {
        const R = this.geometry.launchRadius;
        (0, assert_1.assert)(Math.abs(yawDeg) <= 90 && Math.abs(pitchDeg) <= 90, `yaw and pitch (${yawDeg}, ${pitchDeg}) must be in [-90, 90]`);
        return {
            x: R * Math.cos(pitchDeg * DEG_TO_RAD) * Math.sin(yawDeg * DEG_TO_RAD),
            y: R * Math.cos(pitchDeg * DEG_TO_RAD) * Math.cos(yawDeg * DEG_TO_RAD),
            z: R * Math.sin(pitchDeg * DEG_TO_RAD),
        };
    }
    trainer2lift(pTrainer) {
        const { liftPivot: lpp, liftArmLength: armLen } = this.geometry;
        return (0, util_1.coordChange)(pTrainer, { x: lpp.x, y: lpp.y + armLen, z: lpp.z }, NO_ROTATION);
    }
    lift2trainer(pLift) {
        const { liftPivot: lpp, liftArmLength: armLen } = this.geometry;
        return (0, util_1.coordChange)(pLift, { x: -lpp.x, y: -(lpp.y + armLen), z: -lpp.z }, NO_ROTATION);
    }
    localization2physics(pLocalization) {
        return this.engine.fromLocalization(pLocalization);
    }
    physics2localization(pPhysics) {
        return this.engine.toLocalization(pPhysics);
    }
    trainer2physics(pTrainer, pTrainerPosition, trainerYaw) {
        return this.engine.fromTrainerFrame(pTrainer, { x: pTrainerPosition.x, y: pTrainerPosition.y, yaw: trainerYaw, headHeight: 0 });
    }
    // v1 also subtracts the position's height, which the engine's trainer frame has no use for.
    physics2trainer(pPhysics, pTrainerPosition, trainerYaw) {
        return this.engine.toTrainerFrame({ x: pPhysics.x, y: pPhysics.y, z: pPhysics.z - pTrainerPosition.z }, { x: pTrainerPosition.x, y: pTrainerPosition.y, yaw: trainerYaw, headHeight: 0 });
    }
    // v1 converts by the wheel-to-ball diameter ratio, reading the ball's diameter for both.
    motorspin2ballspin(motorspinRPM) {
        const ballDiam = this.model.ballParams.diameter;
        const wheelDiam = this.model.ballParams.diameter;
        (0, assert_1.assert)(motorspinRPM >= 0, `ball spin in motor rpm ${motorspinRPM} must be >=0`);
        (0, assert_1.assert)(ballDiam > 0, `ball diameter ${ballDiam} must be >0`);
        (0, assert_1.assert)(wheelDiam > 0, `wheel diameter ${wheelDiam} must be >0`);
        return (wheelDiam / ballDiam) * motorspinRPM;
    }
    ballspin2motorspin(ballspinRPM) {
        const ballDiam = this.model.ballParams.diameter;
        const wheelDiam = this.model.ballParams.diameter;
        (0, assert_1.assert)(ballspinRPM >= 0, `ball spin in ball rpm ${ballspinRPM} must be >=0`);
        (0, assert_1.assert)(ballDiam > 0, `ball diameter ${ballDiam} must be >0`);
        (0, assert_1.assert)(wheelDiam > 0, `wheel diameter ${wheelDiam} must be >0`);
        return (ballDiam / wheelDiam) * ballspinRPM;
    }
    motorspin2spinlevel(motorspinRPM) {
        const { maxSpin } = this.rpmLimits;
        (0, assert_1.assert)(motorspinRPM >= 0 && motorspinRPM <= maxSpin, `motor spin RPM (${motorspinRPM}) must in [0, ${maxSpin}]`);
        if (motorspinRPM === 0) {
            return 0;
        }
        // 9.999, not 10: (0, max / 10] is level 1, and so on up
        return Math.floor((motorspinRPM / maxSpin) * 9.999) + 1;
    }
    spinlevel2motorspin(spinLevel) {
        (0, assert_1.assert)(spinLevel >= 0 && spinLevel <= 10 && Number.isInteger(spinLevel), `spin level ${spinLevel} must be an integer in [0,10]`);
        // the middle of the level's tenth of the spin range
        return spinLevel === 0 ? 0 : (spinLevel / 10.0 - 0.05) * this.rpmLimits.maxSpin;
    }
    ballspin2spinlevel(ballspinRPM) {
        return this.motorspin2spinlevel(this.ballspin2motorspin(ballspinRPM));
    }
    spinlevel2ballspin(spinlevel) {
        return this.motorspin2ballspin(this.spinlevel2motorspin(spinlevel));
    }
    maxmotorspin_for_rpmspeed(angle, launch_speed_rpm) {
        if (launch_speed_rpm === 0) {
            return 0;
        }
        const { minSafe, maxSafe } = this.rpmLimits;
        return Math.min(this.maxMotorSpinForLimit(angle, launch_speed_rpm, maxSafe), this.maxMotorSpinForLimit(angle, launch_speed_rpm, minSafe));
    }
    maxspinlevel_for_rpmspeed(angle, launch_speed_rpm) {
        if (launch_speed_rpm === 0) {
            return 0;
        }
        const maxMotorSpin = this.maxmotorspin_for_rpmspeed(angle, launch_speed_rpm);
        const maxLevel = this.motorspin2spinlevel(maxMotorSpin);
        if (maxLevel === 0) {
            return 0;
        }
        return this.spinlevel2motorspin(maxLevel) > maxMotorSpin ? maxLevel - 1 : maxLevel;
    }
    maxspinlevel(angle, speed) {
        return this.maxspinlevel_for_rpmspeed(angle, this.mps2rpm(speed));
    }
    rpm2mps(rpm) {
        (0, assert_1.assert)(rpm >= 0, `rpm is ${rpm}: must be >=0`);
        return this.mechanism.speedForRpm(rpm);
    }
    mps2rpm(mps) {
        (0, assert_1.assert)(mps >= 0, `mps is ${mps}: must be >=0`);
        return this.mechanism.rpmForSpeed(mps);
    }
    rpm2speedspin(rpms) {
        const meanRpm = (rpms.top + rpms.left + rpms.right) / 3.0;
        const speed = this.rpm2mps(meanRpm);
        if (meanRpm === 0 || speed === 0) {
            return { spinAxis: 0, speed, spin: 0, spinLevel: 0 };
        }
        const thrown = this.mechanism.fromMotors(rpms);
        // The mechanism's spin is v1's spin motor RPM, damping applied.
        return {
            spinAxis: thrown.spin.axis,
            speed: thrown.speed,
            spin: this.motorspin2ballspin(thrown.spin.rpm),
            spinLevel: this.motorspin2spinlevel(thrown.spin.rpm),
        };
    }
    speedspin2rpm(speedSpin) {
        const { speed, spin, spinLevel, spinAxis } = speedSpin;
        (0, assert_1.assert)(speed >= 0, `speedspin2rpm: illegal speed ${speed}: must be >=0`);
        (0, assert_1.assert)((spin === null && spinLevel !== null) || (spin !== null && spinLevel === null), "speedspin2rpm: exactly 1 of (spin, spinLevel) must be specified");
        const hasSpin = (spin !== null && spin > 0) || (spinLevel !== null && spinLevel > 0);
        if (!hasSpin) {
            return this.mechanism.toMotors(speed, { rpm: 0, axis: spinAxis });
        }
        const spinRpm = spinLevel !== null ? this.spinlevel2motorspin(spinLevel) : this.ballspin2motorspin(spin);
        (0, assert_1.assert)(spinAxis >= 0 && spinAxis <= 360, `angle ${spinAxis} must be in [0, 360]`);
        return this.mechanism.toMotors(speed, { rpm: spinRpm, axis: spinAxis });
    }
    cameraHeight2HeadHeight(cameraHeightMeters) {
        return this.engine.headHeightForCamera(cameraHeightMeters);
    }
    launchHeight2HeadHeight(launchHeightMeters) {
        return this.engine.headHeightForLaunch(launchHeightMeters);
    }
    launchHeightInches2HeadHeight(launchHeightInches) {
        return this.engine.headHeightForLaunch(launchHeightInches * 0.0254);
    }
    /** v1 maxmotorspin_for_limit: the most spin that keeps every wheel on its side of rpmLimit. */
    maxMotorSpinForLimit(angle, launchSpeedRpm, rpmLimit) {
        const { minSafe: min, maxSafe: max } = this.rpmLimits;
        (0, assert_1.assert)(angle >= 0 && angle <= 360, `angle ${angle} must be in [0, 360]`);
        (0, assert_1.assert)(launchSpeedRpm >= min && launchSpeedRpm <= max, `launch speed RPM ${launchSpeedRpm} must be >=${min} and <= ${max}`);
        (0, assert_1.assert)(rpmLimit >= min && rpmLimit <= max, `RPM limit ${rpmLimit} must be >= ${min} and <= ${max}`);
        if (launchSpeedRpm === 0 || launchSpeedRpm === rpmLimit) {
            return 0;
        }
        const top = snap(Math.cos(angle * DEG_TO_RAD));
        const left = snap(Math.cos(angle * DEG_TO_RAD) - Math.sqrt(3) * Math.sin(angle * DEG_TO_RAD));
        const right = snap(Math.cos(angle * DEG_TO_RAD) + Math.sqrt(3) * Math.sin(angle * DEG_TO_RAD));
        const headroom = launchSpeedRpm - rpmLimit;
        const flip = Math.sign(headroom);
        return Math.min(flip * top < 0 ? -1.5 * headroom * (1.0 / top) : 1e6, flip * left > 0 ? 3.0 * headroom * (1.0 / left) : 1e6, flip * right > 0 ? 3.0 * headroom * (1.0 / right) : 1e6);
    }
}
exports.Conversions = Conversions;
